import { nothing } from 'lit'
import { AsyncDirective } from 'lit/async-directive.js'
import { directive, type ElementPart, PartType } from 'lit/directive.js'
import {
	animationFrameScheduler,
	asapScheduler,
	EMPTY,
	fromEvent,
	merge,
	Observable,
	observeOn,
	of,
	Subject,
	switchMap,
	takeUntil,
} from 'rxjs'
import { fromResizeObserver } from './layout'

export interface OtpOptions {
	/** Number of digit positions. Defaults to six. */
	length?: number
	/** Position of the slot group within the input. Defaults to centered. */
	alignment?: 'left' | 'center'
}

const positionedParents = new WeakMap<HTMLElement, { count: number; position: string }>()

/** One editable value, with persistent per-digit slots underneath its native text. */
class OtpDirective extends AsyncDirective {
	private element?: HTMLElement
	private layer?: HTMLDivElement
	private parent?: HTMLElement
	private paintedWidth?: string
	private length = 6
	private alignment: 'left' | 'center' = 'center'
	private readonly teardown = new Subject<void>()
	private readonly redraw = new Subject<void>()
	private readonly styles = new Map<string, { value: string; priority: string }>()
	private readonly attributes = new Map<string, string | null>()

	render(_options?: OtpOptions) {
		return nothing
	}

	override update(part: ElementPart, [options]: [OtpOptions?]) {
		if (part.type !== PartType.ELEMENT || !(part.element instanceof HTMLElement)) {
			throw new Error('otp must be applied to an input or schmancy-input')
		}
		const length = options?.length ?? 6
		if (!Number.isInteger(length) || length < 1) throw new Error('otp length must be a positive integer')
		this.length = length
		this.alignment = options?.alignment ?? 'center'
		if (!this.element) this.element = part.element

		if (!(this.element instanceof HTMLInputElement)) {
			if (this.element.localName !== 'schmancy-input') throw new Error('otp requires an input or schmancy-input')
			for (const name of ['otp', 'maxlength', 'otp-alignment']) {
				if (!this.attributes.has(name)) this.attributes.set(name, this.element.getAttribute(name))
			}
			this.element.setAttribute('otp', '')
			this.element.setAttribute('maxlength', String(this.length))
			this.element.setAttribute('otp-alignment', this.alignment)
			return nothing
		}

		if (!this.layer && this.isConnected) this.connect()
		this.paint()
		this.redraw.next()
		return nothing
	}

	private connect() {
		if (!(this.element instanceof HTMLInputElement)) return
		this.teardown.next()
		of(this.element)
			.pipe(
				// Element directives first update while their template is still a
				// detached fragment; wait for Lit to attach it and commit later bindings.
				observeOn(asapScheduler),
				switchMap(input => {
					if (!input.parentElement) return EMPTY
					this.parent = input.parentElement
					if (getComputedStyle(this.parent).position === 'static' || positionedParents.has(this.parent)) {
						const held = positionedParents.get(this.parent) ?? { count: 0, position: this.parent.style.position }
						held.count++
						positionedParents.set(this.parent, held)
						this.parent.style.position = 'relative'
					}
					for (const name of ['type', 'inputmode', 'autocomplete', 'maxlength']) {
						if (!this.attributes.has(name)) this.attributes.set(name, input.getAttribute(name))
					}
					input.type = 'text'
					input.inputMode = 'numeric'
					input.autocomplete = 'one-time-code'
					// Separators in a paste must reach normalization before its digit limit.
					input.removeAttribute('maxlength')
					this.layer = document.createElement('div')
					this.layer.setAttribute('aria-hidden', 'true')
					this.layer.setAttribute('part', 'otp-slots')
					this.layer.style.cssText =
						'position:absolute;display:grid;pointer-events:none;direction:ltr;box-sizing:border-box;'
					input.before(this.layer)

					return merge(
						of(undefined),
						this.redraw.pipe(observeOn(asapScheduler)),
						fromResizeObserver(input).pipe(observeOn(animationFrameScheduler)),
						fromResizeObserver(this.parent).pipe(observeOn(animationFrameScheduler)),
						fromEvent(input, 'input', { capture: true }),
						fromEvent(input, 'compositionend'),
						fromEvent(input, 'animationstart'),
						fromEvent(input, 'focus'),
						fromEvent(input, 'blur'),
						fromEvent(input, 'select'),
						fromEvent(input, 'keyup'),
						fromEvent(input, 'pointerup'),
						new Observable<void>(subscriber => {
							const observer = new MutationObserver(() => subscriber.next())
							observer.observe(input, { attributes: true, attributeFilter: ['disabled', 'readonly', 'aria-invalid'] })
							return () => observer.disconnect()
						}),
						fromEvent(input.form ?? input, 'reset').pipe(observeOn(asapScheduler)),
					)
				}),
			)
			.pipe(takeUntil(this.teardown))
			.subscribe(() => this.paint())
	}

	private paint() {
		if (!(this.element instanceof HTMLInputElement) || !this.layer || !this.parent) return
		const input = this.element
		const start = input.selectionStart
		const end = input.selectionEnd
		const digits = input.value.replace(/[^0-9]/g, '').slice(0, this.length)
		if (input.value !== digits) {
			const before = input.value
			input.value = digits
			if (start !== null && end !== null) {
				input.setSelectionRange(
					Math.min(before.slice(0, start).replace(/[^0-9]/g, '').length, this.length),
					Math.min(before.slice(0, end).replace(/[^0-9]/g, '').length, this.length),
					input.selectionDirection ?? undefined,
				)
			}
		}
		const own = (declarations: Record<string, string>) => {
			for (const [name, value] of Object.entries(declarations)) {
				if (!this.styles.has(name)) {
					this.styles.set(name, {
						value: input.style.getPropertyValue(name),
						priority: input.style.getPropertyPriority(name),
					})
				}
				input.style.setProperty(name, value, 'important')
			}
		}
		// Before the box, so a field with no 2D context to measure in keeps the
		// one it had rather than half of this paint's.
		const context = document.createElement('canvas').getContext('2d')
		if (!context) return
		// Measure the caller's responsive width, preserving any new inline binding.
		const savedWidth = this.styles.get('width')
		if (savedWidth) {
			if (input.style.width !== this.paintedWidth) {
				savedWidth.value = input.style.getPropertyValue('width')
				savedWidth.priority = input.style.getPropertyPriority('width')
			} else input.style.setProperty('width', savedWidth.value, savedWidth.priority)
		}
		const fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
		// The box first, with its inline padding and end margin zeroed, so the
		// measure below is the field's own width in every width mode: a rect carries
		// an animating ancestor's scale, and a content-box width carries whatever
		// padding stands — the host's own until this paint replaces it — on a field
		// sized against its margin box, which is what `input.scss` gives the inner
		// input unlayered.
		own({
			position: 'relative',
			'z-index': '1',
			'box-sizing': 'content-box',
			height: '64px',
			'padding-block': '0px',
			'padding-inline-start': '0px',
			'padding-inline-end': '0px',
			'margin-inline-end': '0px',
			border: '0px',
			'font-family': fontFamily,
			'font-weight': '500',
			'font-variant-numeric': 'tabular-nums',
			'text-align': 'left',
			direction: 'ltr',
			background: 'transparent',
			'box-shadow': 'none',
			outline: 'none',
			'caret-color': 'var(--schmancy-sys-color-surface-on, currentColor)',
		})
		const width = parseFloat(getComputedStyle(input).width)
		const gap = Math.min(8, width / (this.length * 6))
		const fontSize =
			this.alignment === 'left'
				? Math.max(16, Math.min(28, Math.min(48, width / (this.length + 1) - gap) * 0.55))
				: Math.max(16, Math.min(32, width / (this.length * 1.4)))
		context.font = `500 ${fontSize}px ${fontFamily}`
		const glyph = context.measureText('0').width
		const advance =
			this.alignment === 'left'
				// Left reserves a trailing glyph advance instead, so its band sits
				// inside the field and the text fits without the overhang below.
				? Math.min(48 + gap, (width + glyph) / (this.length + 1))
				: (width + gap) / this.length
		const cell = advance - gap
		// Native text clips at its padding edge, so a negative indent cuts off
		// the first glyph. Give the trailing advance real content width instead;
		// the end margin returns that extra width and padding to the layout.
		const overhang = this.alignment === 'left' ? 0 : gap
		const padStart = (cell - glyph) / 2
		own({
			...(overhang ? { width: `${width + overhang}px` } : {}),
			'font-size': `${fontSize}px`,
			'letter-spacing': `${advance - glyph}px`,
			'padding-inline-start': `${padStart}px`,
			'padding-inline-end': '0px',
			'text-indent': '0px',
			'margin-inline-end': `${-(padStart + overhang)}px`,
			'clip-path': `inset(0 ${padStart + overhang}px 0 0)`,
		})
		this.paintedWidth = input.style.width
		const focused = input.matches(':focus') && !input.disabled
		const error = input.getAttribute('aria-invalid') === 'true'
		this.layer.style.left = `${input.offsetLeft}px`
		const height = input.offsetHeight
		const slotHeight = this.alignment === 'left' ? Math.min(height, cell * 1.1) : height
		this.layer.style.top = `${input.offsetTop + (height - slotHeight) / 2}px`
		this.layer.style.width = `${this.length * advance - gap}px`
		this.layer.style.height = `${slotHeight}px`
		this.layer.style.gridTemplateColumns = `repeat(${this.length}, minmax(0, 1fr))`
		this.layer.style.gap = `${gap}px`
		this.layer.style.opacity = input.disabled ? '0.4' : '1'
		while (this.layer.children.length > this.length) this.layer.lastElementChild?.remove()
		while (this.layer.children.length < this.length) {
			const slot = document.createElement('span')
			slot.setAttribute('part', 'otp-slot')
			slot.style.cssText =
				'display:grid;place-items:center;min-width:0;box-sizing:border-box;border-radius:var(--schmancy-radius-xl,var(--radius-xl,0.875rem));font-size:18px;background:var(--schmancy-sys-color-surface-containerLow,Canvas);color:var(--schmancy-sys-color-surface-onVariant,GrayText);'
			this.layer.append(slot)
		}
		Array.from(this.layer.children).forEach((slot, index) => {
			if (!(slot instanceof HTMLElement)) return
			const active =
				focused && !input.readOnly && index === Math.min(input.selectionStart ?? digits.length, this.length - 1)
			slot.textContent = index < digits.length ? '' : '·'
			slot.dataset.filled = String(index < digits.length)
			slot.dataset.active = String(active)
			slot.style.border = `1px solid var(--schmancy-sys-color-${error ? 'error-default' : focused ? 'secondary-default' : 'outline'},GrayText)`
			slot.style.boxShadow = active
				? `inset 0 0 0 1px var(--schmancy-sys-color-${error ? 'error-default' : 'secondary-default'},Highlight)`
				: 'none'
		})
	}

	override disconnected() {
		this.teardown.next()
		this.layer?.remove()
		this.layer = undefined
		if (this.element) {
			for (const [name, saved] of this.styles) this.element.style.setProperty(name, saved.value, saved.priority)
			for (const [name, value] of this.attributes) {
				if (value === null) this.element.removeAttribute(name)
				else this.element.setAttribute(name, value)
			}
		}
		this.paintedWidth = undefined
		this.styles.clear()
		this.attributes.clear()
		if (this.parent) {
			const held = positionedParents.get(this.parent)
			if (held && --held.count === 0) {
				this.parent.style.position = held.position
				positionedParents.delete(this.parent)
			}
		}
		this.parent = undefined
	}

	override reconnected() {
		if (this.element instanceof HTMLInputElement) {
			this.connect()
			this.paint()
		} else if (this.element) {
			for (const name of ['otp', 'maxlength', 'otp-alignment'])
				this.attributes.set(name, this.element.getAttribute(name))
			this.element.setAttribute('otp', '')
			this.element.setAttribute('maxlength', String(this.length))
			this.element.setAttribute('otp-alignment', this.alignment)
		}
	}
}

export const otp = directive(OtpDirective)
